import { IV_LENGTH, AUTH_TAG_LENGTH } from './sealed-params.layout'

const ALGORITHM = 'AES-GCM'

const decodeBase64 = (value: string) =>
  Uint8Array.from(atob(value), char => char.charCodeAt(0))

const encodeBase64Url = (bytes: Uint8Array) => {
  let binary = ''
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte)
  })

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

let cachedKey: Promise<CryptoKey> | undefined

/**
 * Chave propria dos filtros (getFiltersSealKey() no lado servidor), base64,
 * independente da SEALED_PARAMS_KEY que sela o token de confirmacao de dados
 * em /confirmacao-dados. Nao e segredo criptografico aqui: o objetivo e so
 * ofuscar o valor pros trackers (GTM, HubSpot, Clarity) que leem
 * `window.location.href`, nao impedir o proprio navegador do usuario de ver
 * o que ele mesmo esta buscando. As duas chaves tem que ficar separadas: se
 * fossem a mesma, qualquer um que extraisse esta chave publica do bundle
 * decifraria tambem um token de confirmacao de dados vazado.
 */
const getKey = () => {
  if (cachedKey) return cachedKey

  const key = process.env.NEXT_PUBLIC_FILTERS_SEAL_KEY

  if (!key) {
    throw new Error(
      'NEXT_PUBLIC_FILTERS_SEAL_KEY is not set. Set it in a gitignored .env.local for local dev.'
    )
  }

  const raw = decodeBase64(key)

  if (raw.length !== 32) {
    throw new Error(
      'NEXT_PUBLIC_FILTERS_SEAL_KEY must decode to 32 bytes for aes-256-gcm. Generate one with `openssl rand -base64 32`.'
    )
  }

  cachedKey = crypto.subtle.importKey('raw', raw, { name: ALGORITHM }, false, [
    'encrypt'
  ])

  return cachedKey
}

/**
 * Mesmo layout binario do `encryptParams` do servidor (iv + authTag + corpo,
 * base64url) para que um valor selado no cliente seja decifravel pelo
 * `decryptParams` do servidor num reload de pagina inteira. A SubtleCrypto
 * devolve o authTag colado no fim do corpo cifrado; aqui ele e separado e
 * recolocado antes do corpo pra bater com o formato do node:crypto.
 */
export async function encryptParamsClient(
  values: Record<string, string>
): Promise<string> {
  const key = await getKey()
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH))
  const data = new TextEncoder().encode(JSON.stringify(values))

  const encrypted = new Uint8Array(
    await crypto.subtle.encrypt({ name: ALGORITHM, iv }, key, data)
  )

  const body = encrypted.slice(0, encrypted.length - AUTH_TAG_LENGTH)
  const authTag = encrypted.slice(encrypted.length - AUTH_TAG_LENGTH)

  const combined = new Uint8Array(iv.length + authTag.length + body.length)
  combined.set(iv, 0)
  combined.set(authTag, iv.length)
  combined.set(body, iv.length + authTag.length)

  return encodeBase64Url(combined)
}
